/**
 * SystemSurveyManager Component
 * Displays the global system survey configuration (read-only for sys_admin)
 *
 * System Survey Architecture:
 * - ONE global survey (entity_id = NULL)
 * - Only uses global questions (no custom questions)
 * - sys_admin manages questions via Question Library
 * - Entities only toggle system survey on/off (no customization)
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    Monitor, Settings, Library, RefreshCw, ExternalLink, Globe
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { usePreferences } from '../../contexts/PreferenceContext';
import { ThemedButton, ThemedCard, ThemedBadge } from '../themed';
import QuestionCard from './QuestionCard';
import axios from '../../axiosConfig';

/**
 * SystemSurveyManager - View global system survey
 *
 * Read-only viewer showing current system survey questions
 * Links to Question Library for editing
 */
const SystemSurveyManager = ({ entityId, onManageQuestions }) => {
    const { t } = useTranslation();
    const { text, cn } = useTheme();
    const { entitySettings } = usePreferences(); // Get entity settings for multi-language support

    // Multi-language settings from entity preferences
    const multiLanguageEnabled = entitySettings?.multi_language_enabled || false;
    const entityDefaultLanguage = entitySettings?.entity_default_language || 'en';

    // State
    const [loading, setLoading] = useState(true);
    const [questions, setQuestions] = useState([]);

    // Load global system survey on mount
    useEffect(() => {
        loadSystemSurvey();
    }, []);

    /**
     * Load global system survey configuration
     */
    const loadSystemSurvey = async () => {
        try {
            setLoading(true);

            // Get global system survey questions
            const response = await axios.get(
                `/api/surveys/library/${entityId}/system?isGlobal=true`
            );
            setQuestions(response.data.data || []);
        } catch (error) {
            console.error('Error loading system survey:', error);
            toast.error(t('errorLoadingSystemSurvey'));
        } finally {
            setLoading(false);
        }
    };

    /**
     * Request the parent survey settings view to open the system question library.
     */
    const handleManageQuestions = () => {
        onManageQuestions?.('system');
    };

    // Loading state
    if (loading) {
        return (
            <div className={cn("flex justify-center items-center h-64", text.secondary)}>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <span className="ml-3">{t('loading')}...</span>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Info card */}
            <ThemedCard className="p-6 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20">
                <div className="flex items-start gap-4">
                    <div className={cn(
                        "w-12 h-12 rounded-full flex items-center justify-center",
                        "bg-purple-500 text-white"
                    )}>
                        <Monitor className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                        <h3 className={cn("text-lg font-semibold mb-2", text.primary)}>
                            {t('globalSystemSurvey')}
                        </h3>
                        <p className={cn("text-sm mb-4", text.secondary)}>
                            {t('globalSystemSurveyDescription')}
                        </p>
                        <div className="flex items-center gap-3">
                            <ThemedButton
                                onClick={handleManageQuestions}
                                variant="primary"
                            >
                                <Library className="w-4 h-4 mr-2" />
                                {t('manageQuestionsInLibrary')}
                                <ExternalLink className="w-3 h-3 ml-1" />
                            </ThemedButton>
                            <ThemedBadge variant="info">
                                <Globe className="w-3 h-3 mr-1" />
                                {questions.length} {t('globalQuestions')}
                            </ThemedBadge>
                        </div>
                    </div>
                </div>
            </ThemedCard>

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h3 className={cn("text-lg font-semibold", text.primary)}>
                        {t('systemSurveyQuestions')}
                    </h3>
                    <p className={cn("text-sm mt-1", text.secondary)}>
                        {t('theseQuestionsAreUsedByAllEntities')}
                    </p>
                </div>
                <ThemedButton
                    onClick={loadSystemSurvey}
                    variant="outline"
                    size="sm"
                >
                    <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
                    {t('refresh')}
                </ThemedButton>
            </div>

            {/* Questions list */}
            {questions.length === 0 ? (
                <ThemedCard className="p-8">
                    <div className={cn("text-center", text.secondary)}>
                        <Monitor className="w-16 h-16 mx-auto mb-4 opacity-50" />
                        <h3 className={cn("text-lg font-semibold mb-2", text.primary)}>
                            {t('noSystemQuestionsYet')}
                        </h3>
                        <p className="mb-4">
                            {t('addQuestionsInQuestionLibrary')}
                        </p>
                        <ThemedButton onClick={handleManageQuestions}>
                            <Library className="w-4 h-4 mr-2" />
                            {t('goToQuestionLibrary')}
                        </ThemedButton>
                    </div>
                </ThemedCard>
            ) : (
                <div className="space-y-4">
                    {questions.map((question, index) => (
                        <QuestionCard
                            key={question.id}
                            question={question}
                            index={index}
                            totalQuestions={questions.length}
                            entityId={null} // Global questions have entityId = null
                            isGlobal={true}
                            canEdit={false} // Read-only view
                            canDelete={false} // Read-only view
                            canReorder={false} // Read-only view
                            multiLanguageEnabled={multiLanguageEnabled}
                            entityDefaultLanguage={entityDefaultLanguage}
                        />
                    ))}
                </div>
            )}

            {/* Footer info */}
            <ThemedCard className="p-4">
                <div className="flex items-center gap-3">
                    <Settings className={cn("w-5 h-5", text.secondary)} />
                    <div className="flex-1">
                        <p className={cn("text-sm", text.secondary)}>
                            {t('systemSurveyFooterInfo')}
                        </p>
                    </div>
                </div>
            </ThemedCard>
        </div>
    );
};

export default SystemSurveyManager;
